import type { ReportPrefillDigest } from './turn-outcome-digest';

/** Join a list into prose: "a", "a and b", or "a, b, and c" (Oxford comma). */
function joinList(parts: string[]): string {
  if (parts.length <= 1) return parts[0] ?? '';
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
}

/** "Talent Discovery" -> "Talent Discovery report"; leave names that already end in "report". */
function reportLabel(reportName: string): string {
  return /\breport$/i.test(reportName.trim())
    ? reportName.trim()
    : `${reportName.trim()} report`;
}

/**
 * Build the one-line report-prefill confirmation directly from the structured
 * digest — grounded by construction, no LLM. Replaces the legacy
 * per-turn streamed narration for report launches.
 */
export function formatReportPrefillNarration(
  digest: ReportPrefillDigest
): string {
  const label = reportLabel(digest.reportName);
  const resolvedPhrase = joinList(
    digest.resolved.map((r) => `${r.parameter} = ${r.value}`)
  );

  if (digest.skipped.length === 0) {
    return resolvedPhrase
      ? `Set up the ${label} with ${resolvedPhrase}.`
      : `Opened the ${label} — choose your parameters to run it.`;
  }

  const skippedTerms = joinList(digest.skipped.map((s) => `'${s.searchTerm}'`));

  if (resolvedPhrase) {
    const skippedParams = joinList([
      ...new Set(digest.skipped.map((s) => s.parameter)),
    ]);
    return `Set up the ${label} with ${resolvedPhrase}. I couldn't match ${skippedTerms} — set ${skippedParams} manually.`;
  }

  return `Opened the ${label}. I couldn't match ${skippedTerms} — set the parameters manually.`;
}
